import React, { useMemo, useState } from 'react';
import BaseModal, {
	BaseModalProps,
} from '../../../Base/Components/BaseModal/BaseModal';
import DiscourseTravelForm from './DiscourseTravelForm';
import { useDiscourseContext } from '../../Services/DiscourseContext';
import useNotification from '../../../../Hooks/useNotification.hook';
import {
	DiscourseTravelFormValues,
	DiscourseTravelItemExtra,
	findDiscourseTravelAttachment,
} from './discourseTravel.helpers';

interface Props extends BaseModalProps {
	patientId: string;
}

const DiscourseTravelModal: React.FC<Props> = ({
	patientId,
	onClose,
	isOpen,
	...modalProps
}) => {
	const { discourseAttachments, handleSaveDiscourseTravel } =
		useDiscourseContext();
	const { showNotificationSuccess } = useNotification();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const existingValues = useMemo(() => {
		if (!isOpen) {
			return undefined;
		}

		const existingTravel = findDiscourseTravelAttachment(discourseAttachments);
		const extra = existingTravel?.extra as DiscourseTravelItemExtra | undefined;

		if (!extra) {
			return undefined;
		}

		return {
			distanceKm: extra.distanceKm,
			fromAddress: extra.fromAddress,
			toAddress: extra.toAddress,
		};
	}, [discourseAttachments, isOpen]);

	const handleSubmit = async (values: DiscourseTravelFormValues) => {
		setIsSubmitting(true);
		try {
			await handleSaveDiscourseTravel(values);
			showNotificationSuccess({
				content: 'component.discourseTravel.saveSuccess',
			});
			onClose?.();
		} catch {
			// error already displayed by handleSaveDiscourseTravel
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<BaseModal
			title='component.discourseTravelModal.title'
			onClose={onClose}
			isOpen={isOpen}
			{...modalProps}>
			<DiscourseTravelForm
				patientId={patientId}
				existingValues={existingValues}
				onSubmit={handleSubmit}
				disableSubmit={isSubmitting}
			/>
		</BaseModal>
	);
};

export default DiscourseTravelModal;
